import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import StickyHorizontalScrollBar from "@saleor/components/StickyHorizontalScrollBar";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { BIG_ROW_NUMBER_SET } from "@saleor/components/TablePagination/constants";
import TablePaginationCustom from "@saleor/components/TablePaginationCustom";
import { PreviewOrderSortField } from "@saleor/createOrder/urls";
import {
  PreviewCustomerOrderQuery,
  PreviewDomesticPageOrderQuery
} from "@saleor/graphql";
import { maybe, renderCollection } from "@saleor/misc";
import { ListProps, ListSettings, RelayToFlat, SortPage } from "@saleor/types";
import { getSalesUnit, SALE_CATEG_1 } from "@saleor/utils/constants";
import { formatDate } from "@saleor/utils/date";
import { getArrowDirection } from "@saleor/utils/sort";
import classNames from "classnames";
import React, { FC, useRef } from "react";
import { useIntl } from "react-intl";

import useStyles from "./styles";

export interface IPreviewOrderTable
  extends ListProps,
    SortPage<PreviewOrderSortField> {
  previewOrderLines: RelayToFlat<
    | PreviewCustomerOrderQuery["previewCustomerOrder"]["previewOrderLines"]
    | PreviewDomesticPageOrderQuery["previewDomesticPageOrder"]["previewOrderLines"]
  >;

  settings: ListSettings;
  onPreviousFirstPage: () => void;
  onNextLastPage: () => void;
  totalCount: number | null;
  latestPageItemNumber: number | null;
}

const PreviewOrderTable: FC<IPreviewOrderTable> = ({
  previewOrderLines,
  settings,
  pageInfo,
  disabled,
  onPreviousPage,
  onNextPage,
  onNextLastPage,
  onPreviousFirstPage,
  onUpdateListSettings,
  latestPageItemNumber,
  totalCount,
  sort,
  onSort
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const isAdmin = previewOrderLines?.[0]?.__typename === "PreviewTempOrderLine";

  return (
    <>
      <div className={classes.root} ref={containerRef}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCellHeader
                direction={
                  sort.sort === PreviewOrderSortField.ITEM_NO
                    ? getArrowDirection(sort.asc)
                    : undefined
                }
                arrowPosition="right"
                onClick={() => onSort(PreviewOrderSortField.ITEM_NO)}
                textAlign="center"
                className={classes.col100}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({
                    defaultMessage: "Item No."
                  })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="center" className={classes.col190}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Material Code" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader textAlign="center" className={classes.col340}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "รายการสินค้า" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign={isAdmin ? "right" : "center"}
                className={isAdmin ? "" : classes.col160}
                option="หน่วย"
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "จำนวน" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="right"
                {...(isAdmin
                  ? { option: "หน่วย" }
                  : { className: classes.col120 })}
              >
                <MyTypography variant="subtitle3">
                  {isAdmin
                    ? intl.formatMessage({ defaultMessage: "น้ำหนัก" })
                    : intl.formatMessage({ defaultMessage: "น้ำหนัก (ตัน)" })}
                </MyTypography>
              </TableCellHeader>
              {previewOrderLines?.length > 0 &&
                previewOrderLines[0].__typename === "PreviewTempOrderLine" && (
                  <>
                    <TableCellHeader
                      textAlign="right"
                      className={classes.col120}
                    >
                      <MyTypography variant="subtitle3">
                        {intl.formatMessage({ defaultMessage: "Plant" })}
                      </MyTypography>
                    </TableCellHeader>
                    <TableCellHeader
                      textAlign="center"
                      className={classes.col180}
                    >
                      <MyTypography variant="subtitle3">
                        {intl.formatMessage({
                          defaultMessage: "Original Date"
                        })}
                      </MyTypography>
                    </TableCellHeader>
                  </>
                )}
              <TableCellHeader textAlign="center" className={classes.col180}>
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Request Date" })}
                </MyTypography>
              </TableCellHeader>
              <TableCellHeader
                textAlign="center"
                className={classNames(classes.col180, classes.estDateMatch)}
              >
                <MyTypography variant="subtitle3">
                  {intl.formatMessage({ defaultMessage: "Confirm Date" })}
                </MyTypography>
              </TableCellHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {renderCollection(previewOrderLines, (item, index) => (
              <TableRow className={classes.rowClickable} key={index}>
                <TableCell align="center">
                  <MyTypography variant="body3" className={classes?.itemNo}>
                    {maybe(() => item?.itemNo)}
                  </MyTypography>
                </TableCell>
                <TableCell align="center">
                  {maybe(() =>
                    item?.__typename === "PreviewCustomerTempOrderLine"
                      ? item?.materialVariantCode
                      : item?.variant?.code
                  )}
                </TableCell>
                <TableCell align="left">
                  <span>
                    {maybe(() => item?.descriptionEn)}
                    <br />
                  </span>
                  <span style={{ color: item?.logErrors ? "red" : "green" }}>
                    {item?.logErrors
                      ? `(${item.logErrors})`
                      : item?.logOldProduct
                      ? `(${item.logOldProduct})`
                      : null}
                  </span>
                </TableCell>
                <TableCell
                  {...(isAdmin
                    ? { align: "right", className: classes.colMin160 }
                    : { align: "center" })}
                >
                  {isAdmin && item?.__typename === "PreviewTempOrderLine" ? (
                    <>
                      <span style={{ textAlign: "center" }}>
                        {SALE_CATEG_1(getSalesUnit(item.unit, item.prcGroup1))
                          ? !!item?.quantity && (
                              <DecimalFormat
                                value={+item?.quantity}
                                isGrayDecimal={true}
                                countDecimal={3}
                              />
                            )
                          : maybe(() => item?.quantity)}
                      </span>
                      <span className={classes.colQtyRight}>
                        {maybe(() => getSalesUnit(item.unit, item.prcGroup1))}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className={classes.colQty}>
                        {maybe(() => item?.quantity)}
                      </span>
                      <span>
                        {maybe(() =>
                          item?.__typename === "PreviewCustomerTempOrderLine"
                            ? item?.contractProduct?.product?.salesUnit
                            : item?.variant?.salesUnit
                        )}
                      </span>
                    </>
                  )}
                </TableCell>
                {isAdmin && item?.__typename === "PreviewTempOrderLine" ? (
                  <TableCell align="right" className={classes.colMin160}>
                    <span style={{ margin: "auto" }}>
                      {item?.netWeightTon && (
                        <DecimalFormat
                          value={+item?.netWeightTon}
                          isGrayDecimal={true}
                          countDecimal={3}
                        />
                      )}
                    </span>
                    <span className={classes.colQtyRight}>
                      {maybe(() => item?.weightUnitTon)}
                    </span>
                  </TableCell>
                ) : (
                  <TableCell align="right">
                    {!!item?.orderQuantityTon && (
                      <DecimalFormat
                        value={+item?.orderQuantityTon}
                        isGrayDecimal={true}
                        countDecimal={3}
                      />
                    )}
                  </TableCell>
                )}
                {item?.__typename === "PreviewTempOrderLine" && (
                  <>
                    <TableCell align="right">
                      {maybe(() => item.plant)}
                    </TableCell>
                    <TableCell align="center">
                      {maybe(() => formatDate(item?.originalRequestDate))}
                    </TableCell>
                  </>
                )}
                <TableCell align="center">
                  {maybe(() =>
                    formatDate(
                      item?.__typename === "PreviewCustomerTempOrderLine"
                        ? item?.originalRequestDate
                        : item?.requestDate
                    )
                  )}
                </TableCell>
                <TableCell
                  align="center"
                  className={
                    (item?.__typename === "PreviewCustomerTempOrderLine"
                      ? item?.requestDeliveryDate
                      : item?.requestDate) === item?.confirmedDate
                      ? classes.estDateMatch
                      : classes.estDateNotMatch
                  }
                >
                  {maybe(() => formatDate(item?.confirmedDate))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <StickyHorizontalScrollBar container={containerRef} bottom={160} />
      </div>
      <Box className={classes.paginateFooter}>
        <TablePaginationCustom
          disabled={disabled}
          settings={settings}
          hasNextPage={pageInfo && !disabled ? pageInfo.hasNextPage : false}
          onNextPage={onNextPage}
          onPreviousPage={onPreviousPage}
          onPreviousFirstPage={onPreviousFirstPage}
          onNextLastPage={onNextLastPage}
          onUpdateListSettings={onUpdateListSettings}
          hasPreviousPage={
            pageInfo && !disabled ? pageInfo.hasPreviousPage : false
          }
          selectColor="#007E92"
          latestPageItemNumber={latestPageItemNumber}
          totalCount={totalCount || 0}
          rowNumberSet={BIG_ROW_NUMBER_SET}
          isBackFistPage
          isNextLastPage
        />
      </Box>
    </>
  );
};
export default PreviewOrderTable;
